import type { ComponentType } from "react";
import { LeftOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../store/hooks";
import ModuleSidebarLink from "./ModuleSidebarLink";

export type ModuleSidebarPage = {
  // URL на страницата — и ключът ѝ в менюто.
  path: string;
  // Правото, без което пунктът не се показва и страницата не се отваря.
  permission: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
};

export type ModuleSidebarSection = { title: string | null; pages: ModuleSidebarPage[] };

type ModuleSidebarProps = {
  // Подзаглавието под „UnrealSoft" — напр. „Модул Конфигурация".
  moduleLabel: string;
  // Само пунктовете, за които потребителят има право.
  sections: ModuleSidebarSection[];
  activePath: string;
  onSelect: (path: string) => void;
  collapsed: boolean;
  onToggle: () => void;
  isNarrow: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
};

/**
 * Страничното меню на модул със страници по право (Конфигурация, Резервации):
 * „Към модулите", марката с името на модула, пунктовете и „Свий" долу. На
 * телефон е плъзгащ се панел (отваря се от бутона ☰ в лентата на страницата).
 */
const ModuleSidebar = ({
  moduleLabel,
  sections,
  activePath,
  onSelect,
  collapsed,
  onToggle,
  isNarrow,
  mobileOpen,
  onMobileClose,
}: ModuleSidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const access = useAppSelector((state) => state.auth.access);
  const effectiveCollapsed = isNarrow ? false : collapsed;

  if (isNarrow && !mobileOpen) return null;

  const backToModules = (
    <button
      type="button"
      onClick={() => navigate("/")}
      className="flex cursor-pointer items-center gap-2 rounded-md border-none bg-transparent px-2.5 py-1.5 text-left text-[12px] font-medium text-body"
    >
      <LeftOutlined className="text-[11px]" />
      {t("moduleSidebar.backToModules")}
    </button>
  );

  return (
    <aside
      className={`flex shrink-0 flex-col border-r border-shade-2 bg-sidebar px-3 py-4 font-pos-sans text-body ${
        isNarrow
          ? "dk-elevated fixed inset-y-0 left-0 z-50 w-[280px] shadow-[0_10px_30px_rgba(17,17,17,0.2)]"
          : `relative transition-[width] duration-[180ms] ease-[ease] ${effectiveCollapsed ? "w-[68px]" : "w-[248px]"}`
      }`}
    >
      {isNarrow ? (
        <div className="mb-[14px] flex items-center justify-between">
          {backToModules}
          <button
            type="button"
            onClick={onMobileClose}
            aria-label="Close menu"
            className="cursor-pointer border-none bg-transparent p-1.5 text-[16px] leading-none text-body"
          >
            ✕
          </button>
        </div>
      ) : effectiveCollapsed ? (
        <Tooltip title={t("moduleSidebar.backToModules")} placement="right">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label={t("moduleSidebar.backToModules")}
            className="mb-[14px] flex cursor-pointer items-center justify-center border-none bg-transparent py-1.5 text-body"
          >
            <LeftOutlined className="text-[13px]" />
          </button>
        </Tooltip>
      ) : (
        <div className="mb-[14px]">{backToModules}</div>
      )}

      <div
        className={`mb-[14px] flex items-center gap-2.5 border-b border-shade-2 pb-4 pt-1 ${
          effectiveCollapsed ? "justify-center" : "justify-start px-2.5"
        }`}
      >
        <img src="/logo-ts.png" alt="UnrealSoft" className="size-8 object-contain" />
        {!effectiveCollapsed && (
          <div className="min-w-0 leading-[1.2]">
            <div
              className={`text-[15px] font-semibold tracking-[-0.1px] ${access ? "text-brand-deep-ink" : "text-ink"}`}
            >
              UnrealSoft
            </div>
            <div className="mt-0.5 whitespace-nowrap text-[12px] text-muted">{moduleLabel}</div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-4">
            {section.title && !effectiveCollapsed && (
              <div className="px-2.5 pb-1.5 text-[9.5px] font-bold uppercase tracking-[1.4px] text-muted">
                {section.title}
              </div>
            )}
            {section.pages.map((page) => (
              <ModuleSidebarLink
                key={page.path}
                page={page}
                active={activePath === page.path}
                collapsed={effectiveCollapsed}
                onClick={() => onSelect(page.path)}
              />
            ))}
          </div>
        ))}
      </div>

      {!isNarrow && (
        <div className="mt-2 border-t border-shade-2 pt-2.5">
          <Tooltip
            title={effectiveCollapsed ? t("moduleSidebar.expandMenu") : t("moduleSidebar.collapseMenu")}
            placement="right"
          >
            <button
              type="button"
              onClick={onToggle}
              aria-label={effectiveCollapsed ? t("moduleSidebar.expandMenu") : t("moduleSidebar.collapseMenu")}
              className={`flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent py-[9px] text-[12px] font-medium text-muted transition-colors duration-[120ms] hover:bg-shade-1 hover:text-ink ${
                effectiveCollapsed ? "justify-center" : "justify-start px-2.5"
              }`}
            >
              {effectiveCollapsed ? (
                <MenuUnfoldOutlined className="text-[15px]" />
              ) : (
                <>
                  <MenuFoldOutlined className="text-[15px]" />
                  <span>{t("moduleSidebar.collapse")}</span>
                </>
              )}
            </button>
          </Tooltip>
        </div>
      )}
    </aside>
  );
};

export default ModuleSidebar;
